import React, { useRef, useEffect, ReactElement, Fragment } from 'react';
import { MemoButtonPlusCreate } from 'components/general/ButtonPlusCreate';
import TableSkeleton from 'components/table/skeleton';
import { useStyles } from './styles';
import { resize } from './helpers';
import { TableAnchorProps } from './types';
import { WithLoadingLog } from 'components/general/loaders/Spinner/Spinner';
import { Box } from '@mui/material';

type ResizeObserverChange = {
  target: Element;
  contentRect: {
    width: number;
    height: number;
  };
};

function TableAnchor(props: TableAnchorProps): ReactElement {
  const tableWrapperRef = useRef<HTMLDivElement | null>(null);
  const {
    canCreate,
    creationAction,
    loading,
    headerWithCreation,
    TableComponent,
    SearchInput,
    CreationComponent,
    children,
    DataGridActions,
  } = props;
  const classes = useStyles();
  const [width, setWidth] = React.useState(0);
  const [height, setHeight] = React.useState(0);

  useEffect(() => {
    const ResizeObserver = (window as any).ResizeObserver;
    if (!ResizeObserver) return;
    if (!tableWrapperRef.current) return;
    const theRef = tableWrapperRef.current;
    let timeout: ReturnType<typeof setTimeout>;
    const resizeObserver = new ResizeObserver(
      (changes: ResizeObserverChange[]) => {
        clearTimeout(timeout);
        timeout = setTimeout(
          () => resize(changes, setWidth, setHeight, tableWrapperRef),
          100
        );
      }
    );

    resizeObserver.observe(theRef);
    resizeObserver.observe(document.body);
    return (): void => {
      clearTimeout(timeout);
      resizeObserver.unobserve(theRef);
      resizeObserver.unobserve(document.body);
    };
  }, []);

  // First measurement of the table wrapper; the resize observer handles later changes.
  useEffect(() => {
    setWidth(tableWrapperRef.current?.clientWidth || 0);
    setHeight(tableWrapperRef.current?.clientHeight || 0);
  }, []);

  return (
    <Fragment>
      {Boolean(headerWithCreation) && (
        <Box className={classes.head}>
          <Box className={classes.leftSection}>
            {CreationComponent ? (
              <CreationComponent
                headerWithCreation={headerWithCreation}
                canCreate={canCreate}
                creationAction={creationAction}
              />
            ) : (
              <MemoButtonPlusCreate
                id={headerWithCreation!.id}
                textId={headerWithCreation!.textId}
                dataQaId={headerWithCreation!.dataQaId}
                canCreate={canCreate}
                creationAction={creationAction}
              />
            )}
            {SearchInput !== undefined && <SearchInput />}
          </Box>
          <Box className={classes.rightSection}>
            {DataGridActions !== undefined && <DataGridActions />}
          </Box>
        </Box>
      )}
      {children}
      <Box className={classes.relativeWrapper}>
        <Box className={classes.absoluteWrapper} ref={tableWrapperRef}>
          {!loading ? (
            <TableComponent width={width} height={height} />
          ) : (
            <WithLoadingLog reason='TableAnchor loading'>
              <TableSkeleton />
            </WithLoadingLog>
          )}
        </Box>
      </Box>
    </Fragment>
  );
}

export const MemoTableAnchor = React.memo(TableAnchor);
